import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isUcanApp, getUcanAppPath } from '@/lib/ucanApp';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const oauthState = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const nativeReturn = searchParams.get('ucan_native') === '1';

    /*
     * ============================================================
     * IDENTIFICACIÓN DEL FLUJO
     * ============================================================
     *
     * WEB:
     *   state=ucan_web
     *
     * APP -> SAFARI:
     *   state=ucan_app_ios
     *
     * APP -> WEBVIEW después del deep link:
     *   ucan_native=1
     *
     * IMPORTANTE:
     * No usamos localStorage para decidir si una página web
     * es la app, porque esa marca puede quedar persistida
     * en Safari/navegador.
     */

    const isAppSafari = oauthState === 'ucan_app_ios';
    const isNativeReturn = nativeReturn;

    /*
     * En un WebView nativo que llegue directamente al callback
     * también permitimos isUcanApp(), pero solamente cuando Google
     * no entregó un state explícito de web.
     */
    const isNativeWebView =
      !oauthState &&
      !nativeReturn &&
      isUcanApp();

    const isApp =
      isAppSafari ||
      isNativeReturn ||
      isNativeWebView;

    if (isApp) {
      localStorage.setItem('ucan_app', 'ios');
    } else {
      /*
       * Si estamos en WEB NORMAL, limpiamos cualquier marca vieja
       * que haya quedado de una prueba anterior.
       */
      localStorage.removeItem('ucan_app');
    }

    const appPath = (path) =>
      isApp ? getUcanAppPath(path) : path;

    /*
     * ============================================================
     * APP -> SAFARI -> GOOGLE
     * ============================================================
     *
     * Google volvió a Safari con state=ucan_app_ios.
     *
     * Todavía NO hacemos el intercambio del código.
     * Devolvemos el código a la aplicación mediante:
     *
     * ucan://auth/google/callback?code=...
     */
    if (isAppSafari) {
      if (errorParam) {
        window.location.replace(
          `ucan://auth/google/callback?error=${encodeURIComponent(errorParam)}`
        );
        return;
      }

      if (!code) {
        setError('No se recibio respuesta de Google');
        toast.error('No se recibio respuesta de Google');
        return;
      }

      const deepLink =
        `ucan://auth/google/callback?code=${encodeURIComponent(code)}`;

      console.log(
        'Google App: devolviendo callback a U-CAN'
      );

      window.location.replace(deepLink);
      return;
    }

    /*
     * ============================================================
     * ERRORES
     * ============================================================
     */
    if (errorParam) {
      setError('Inicio de sesion con Google cancelado');
      toast.error('Inicio de sesion con Google cancelado');

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);

      return;
    }

    if (!code) {
      setError('No se recibio respuesta de Google');
      toast.error('No se recibio respuesta de Google');

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);

      return;
    }

    /*
     * ============================================================
     * INTERCAMBIO DEL CÓDIGO
     * ============================================================
     *
     * Esto ocurre:
     *
     * 1. WEB NORMAL
     * 2. WEBVIEW NATIVO después de volver desde Safari
     *    con ucan_native=1
     */
    const exchangeCode = async () => {
      try {
        const redirectUri =
          'https://u-can.cl/auth/google/callback';

        console.log(
          'Google: intercambiando authorization code'
        );

        const response = await api.post('/auth/google', {
          code,
          redirect_uri: redirectUri
        });

        localStorage.setItem(
          'jwt_token',
          response.data.token
        );

        toast.success('Sesión iniciada con Google');

        const registerRole =
          localStorage.getItem('register_role');

        localStorage.removeItem('register_role');

        const redirectTo =
          localStorage.getItem(
            'ucan_after_login_redirect'
          );

        if (redirectTo) {
          localStorage.removeItem(
            'ucan_after_login_redirect'
          );

          navigate(appPath(redirectTo), {
            replace: true
          });

          return;
        }

        if (registerRole === 'provider') {
          navigate(
            appPath('/provider/register'),
            { replace: true }
          );
        } else if (registerRole === 'client') {
          navigate(
            appPath('/dashboard?newClientPet=1'),
            { replace: true }
          );
        } else {
          navigate(
            appPath('/dashboard'),
            { replace: true }
          );
        }

      } catch (err) {
        console.error(
          'Error Google callback:',
          err
        );

        const msg =
          err.response?.data?.detail ||
          'Error al iniciar sesion con Google';

        setError(msg);
        toast.error(msg);

        setTimeout(() => {
          navigate('/login', {
            replace: true
          });
        }, 3000);
      }
    };

    exchangeCode();
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white"
      data-testid="google-callback-page"
    >
      <div className="text-center space-y-4">

        {error ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 text-xl font-bold">
                !
              </span>
            </div>

            <p className="text-gray-700 font-medium">
              {error}
            </p>

            <p className="text-sm text-gray-500">
              Redirigiendo al inicio de sesión...
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#E6202E]" />

            <p className="text-gray-700 font-medium">
              Procesando autenticación con Google...
            </p>
          </>
        )}

      </div>
    </div>
  );
};

export default GoogleCallback;
